// Constants
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
// Styles
import { memoizeFunction } from "@fluentui/react";
import { highContrastColor, highContrastHighlightTextColor, highContrastSelectionText } from "../../../utilities/HighContrast.styles";
import { backgroundColorHover, backgroundColorNeutralPrimary, fontColorNeutralPrimary, fontColorNeutralTertiary } from "../../../utilities/Mixin.styles";
/**
 * Calendar picker highlighted style
 * @param theme The current theme
 */ const calendarPickerHighlighted = memoizeFunction((theme)=>{
    let color = "";
    let backgroundColor = "";
    switch(theme.name){
        case PlannerTheme.Light:
            color = Colors.Black;
            backgroundColor = Colors.TeamsAppBrand14;
            break;
        case PlannerTheme.Dark:
            color = Colors.White;
            backgroundColor = Colors.TeamsDarkPurple;
            break;
        case PlannerTheme.HighContrast:
            color = Colors.Black;
            backgroundColor = Colors.TeamsContrastSelectedColor;
            break;
        default:
            break;
    }
    return {
        color,
        backgroundColor
    };
});
/**
 * Helper to get styles for the Calendar
 * @param theme The current theme
 */ export const getCalendarStyles = memoizeFunction((theme)=>{
    const root = [
        backgroundColorNeutralPrimary(theme),
        {
            "&:not(.ms-CalendarDay-daySelected)": {
                button: {
                    ":focus, :hover": [
                        fontColorNeutralPrimary(theme),
                        backgroundColorHover(theme),
                        highContrastSelectionText(),
                        {
                            "i > svg": highContrastHighlightTextColor()
                        }
                    ]
                }
            }
        }
    ];
    const goTodayButton = [
        fontColorNeutralPrimary(theme),
        {
            marginBottom: "21px",
            ":hover, :focus": [
                backgroundColorHover(theme),
                highContrastSelectionText(),
                {
                    borderRadius: "2px"
                }
            ]
        }
    ];
    return {
        root,
        goTodayButton
    };
});
/**
 * Helper to get styles for the Calendar Months section
 * @param theme The current theme
 */ export const getCalendarMonthStyles = memoizeFunction((theme)=>{
    const buttonStyle = [
        fontColorNeutralPrimary(theme),
        {
            ":hover": backgroundColorHover(theme),
            "> i": {
                display: "block"
            }
        }
    ];
    return {
        currentItemButton: buttonStyle,
        itemButton: buttonStyle,
        navigationButton: buttonStyle
    };
});
/**
 * Helper to get styles for the Calendar Days section
 * @param theme The current theme
 */ export const getCalendarDayStyles = memoizeFunction((theme)=>{
    let datePickerDayTodayColor = "";
    let datePickerDayTodayBGColor = "";
    switch(theme.name){
        case PlannerTheme.Light:
            datePickerDayTodayColor = Colors.White;
            datePickerDayTodayBGColor = Colors.TeamsAppBrand00;
            break;
        case PlannerTheme.Dark:
            datePickerDayTodayColor = Colors.Black;
            datePickerDayTodayBGColor = Colors.TeamsDarkPurple;
            break;
        case PlannerTheme.HighContrast:
            datePickerDayTodayColor = Colors.Black;
            datePickerDayTodayBGColor = Colors.White;
            break;
        default:
            break;
    }
    const headerIconButton = [
        fontColorNeutralPrimary(theme),
        {
            ":hover": backgroundColorHover(theme),
            "> i": {
                display: "block"
            }
        }
    ];
    const weekDayLabelCell = [
        fontColorNeutralPrimary(theme),
        highContrastColor(),
        {
            cursor: "default"
        }
    ];
    const monthAndYear = fontColorNeutralPrimary(theme);
    const dayOutsideNavigatedMonth = [
        fontColorNeutralTertiary(theme),
        highContrastColor()
    ];
    const dayCell = [
        fontColorNeutralPrimary(theme),
        {
            ":hover": {
                backgroundColor: "transparent !important"
            },
            "> button:hover": [
                backgroundColorHover(theme),
                highContrastSelectionText()
            ]
        }
    ];
    const daySelected = [
        {
            "> button": [
                calendarPickerHighlighted(theme),
                highContrastSelectionText()
            ],
            "&.ms-CalendarDay-hoverStyle": {
                backgroundColor: "transparent !important"
            },
            ":hover": {
                backgroundColor: "transparent !important"
            },
            "::before": {
                border: "none !important"
            },
            backgroundColor: `${datePickerDayTodayBGColor} !important`,
            color: `${datePickerDayTodayColor} !important`
        }
    ];
    const dayIsToday = {
        color: `${datePickerDayTodayColor} !important`,
        backgroundColor: `${datePickerDayTodayBGColor} !important`,
        ":focus, :hover, :active": {
            borderRadius: "2px"
        }
    };
    return {
        headerIconButton,
        weekDayLabelCell,
        monthAndYear,
        dayOutsideNavigatedMonth,
        dayCell,
        daySelected,
        dayIsToday
    };
});
